import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import UserInfo from "src/components/common/userInfo";
import Footer from "src/components/common/frame/footer";
import { useMyStore } from "src/components/main/mainStore";
import { MyFormData, myAxios, scroll } from "src/components/common/functions";
import OrganizationForm from "./organizationForm";
//--------------------------------------------------------------------------------------------
export default function Detail({
    updateDateTime, 
    org, 
    history,
    setCurrentPage
}){
    //--------------------------------------------------------------------------------------------
    const path = history.location.pathname;
    const path1 = decodeURIComponent(path.split("/")[1] ?? ""); // title
    const path2 = decodeURIComponent(path.split("/")[2] ?? ""); // hash | "Add"
    const path3 = decodeURIComponent(path.split("/")[3] ?? ""); // "Edit"
    //--------------------------------------------------------------------------------------------
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const organizations = useMyStore(state => state.organizations);
    const setOrganizations = useMyStore(state => state.setOrganizations);
    const organizationDetails = useMyStore(state => state.organizationDetails);
    const setOrganizationDetails = useMyStore(state => state.setOrganizationDetails);
    //--------------------------------------------------------------------------------------------
    const [showLoading, setShowLoading] = useState(false);
    //--------------------------------------------------------------------------------------------
    const curOrg = useMemo(() => organizationDetails[org.organizationHashKey]?({...org, ...organizationDetails[org.organizationHashKey]}):org, [org, organizationDetails]);
    const address = [
        curOrg.street, 
        curOrg.city, 
        curOrg.state, 
        curOrg.postalCode, 
        curOrg.country
    ].filter(item => item).join(", ");
    //--------------------------------------------------------------------------------------------
    const [contactsPerPage, setContactsPerPage] = useState(10);
    const [currentContactPage, setCurrentContactPage] = useState(1);
    const indexOfLastContact = currentContactPage * contactsPerPage;
    const indexOfFirstContact = indexOfLastContact - contactsPerPage;
    const contacts = useMemo(() => curOrg?.contacts ?? [], [curOrg]);
    const contactPageNumbers = Array.from({length: Math.ceil(contacts.length / contactsPerPage)}, (_, i) => (i + 1));
    //--------------------------------------------------------------------------------------------
    const [opportunitiesPerPage, setOpportunitiesPerPage] = useState(10);
    const [currentOpportunityPage, setCurrentOpportunityPage] = useState(1);
    const indexOfLastOpportunity = currentOpportunityPage * opportunitiesPerPage;
    const indexOfFirstOpportunity = indexOfLastOpportunity - opportunitiesPerPage;
    const opportunities = useMemo(() => curOrg?.opportunities ?? [], [curOrg]);
    const opportunityPageNumbers = Array.from({length: Math.ceil(opportunities.length / opportunitiesPerPage)}, (_, i) => (i + 1));
    //--------------------------------------------------------------------------------------------
    const [projectsPerPage, setProjectsPerPage] = useState(10);
    const [currentProjectPage, setCurrentProjectPage] = useState(1);
    const indexOfLastProject = currentProjectPage * projectsPerPage;
    const indexOfFirstProject = indexOfLastProject - projectsPerPage;
    const projects = useMemo(() => curOrg?.projects ?? [], [curOrg]);
    const projectPageNumbers = Array.from({length: Math.ceil(projects.length / projectsPerPage)}, (_, i) => (i + 1));
    //--------------------------------------------------------------------------------------------
    const handleAddContact = () => history.push(`/Contacts/Add/${curOrg.organizationHashKey}`);
    const handleEditOpen = () => history.push(`/${path1}/${curOrg.organizationHashKey}/Edit`);
    const handleDelete = async e => {
        if(window.confirm("Are you sure to delete this user?")){
            const formData = new MyFormData();
            const organizationHashKey = e.currentTarget.id;
            formData.append("organizationHashKey", organizationHashKey);
            //--------------------------------------
            setShowLoader(true);
            const res = await myAxios("organizations/deleteOrg.php", formData);
            setShowLoader(false);
            if(!res) return;
            //--------------------------------------
            setOrganizations(organizations.data.filter(org => org.organizationHashKey !== organizationHashKey));
            history.replace(`/${path1}`);
            setCurrentPage(1);
            scroll(null);
        }
    }
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        if(path2 === "" || (organizationDetails[curOrg.organizationHashKey] && (updateDateTime["OrganizationDetails"] === organizationDetails.updatedAt))) return;
        let isMounted = true;
        const controller = new AbortController();
        const getData = async () => {
            const formData = new MyFormData();
            formData.append("organizationHashKey", curOrg.organizationHashKey);
            //--------------------------------------
            setShowLoading(true);
            const resOrganizationDetail = await myAxios("organizations/readOrgDetail.php", formData, undefined, controller.signal);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resOrganizationDetail) return;
            //--------------------------------------
            setOrganizationDetails(
                curOrg.organizationHashKey, 
                resOrganizationDetail.data, 
                updateDateTime["OrganizationDetails"]
            );
        }
        getData();
        return () => {
            isMounted = false;
            controller.abort();
            setShowLoader(false);
        }
    }, [curOrg.organizationHashKey, setShowLoader, organizationDetails, setOrganizationDetails, updateDateTime, path2])
    //--------------------------------------------------------------------------------------------
    return (
        showLoading?<div className = "cssLoading border rounded p-4">Loading...</div>:
        <div className = "border rounded">
            <div className = "cssSeperateWhenWide pl-3">
                <table className = "table table-fixed flex-grow-1 maxWidth-50 mr-3">
                    <tbody className = "noTableTopBorder">
                        {address?
                        <tr>
                            <th>Address</th>
                            <td>
                                {address}
                            </td>
                        </tr>
                        :<></>}
                        {curOrg.creatorEmail?
                        <tr>
                            <th>Creator</th>
                            <td>
                                <UserInfo name = {curOrg.creator} email = {curOrg.creatorEmail}/>
                                <span className = "ml-3">{curOrg.dateCreated}</span>
                            </td>
                        </tr>
                        :<></>}
                        {curOrg.updaterEmail?
                        <tr>
                            <th>Updater</th>
                            <td>
                                <UserInfo name = {curOrg.updater} email = {curOrg.updaterEmail}/>
                                <span className = "ml-3">{curOrg.dateUpdated}</span>
                            </td>
                        </tr>
                        :<></>}
                        {contacts.length?
                        <tr id = "contactsTR">
                            <th>Employees</th>
                            <td className = "position-relative pb10">
                                {contacts.slice(indexOfFirstContact, indexOfLastContact).map((c, key) => <li key = {key}><Link to = {`/Contacts/${c.contactHashKey}`}>{c.contactName}</Link></li>) ?? ""}
                                {!showLoading && contacts.length?
                                <Footer
                                    pageSelectId = "employees"
                                    length = {contacts.length}
                                    pageNumbers = {contactPageNumbers}
                
                                    currentPage = {currentContactPage}
                                    setCurrentPage = {setCurrentContactPage}
                                    itemsPerPage = {contactsPerPage}
                                    setItemsPerPage = {setContactsPerPage}

                                    parentId = {"contactsTR"}
                                />:null}
                            </td>
                        </tr>
                        :<></>}
                        {opportunities.length?
                        <tr id = "opportunitiesTR">
                            <th>Opportunities</th>
                            <td className = "position-relative pb10">
                                {opportunities.slice(indexOfFirstOpportunity, indexOfLastOpportunity).map((c, key) => <li key = {key}><Link to = {`/Opportunities/${c.opportunityHashKey}`}><span>{c.opportunityName}</span><span className = "ml-2 badge badge-primary">{c.currentState}</span></Link></li>)}
                                {!showLoading && opportunities.length?
                                <Footer
                                    pageSelectId = "opportunities"
                                    length = {opportunities.length}
                                    pageNumbers = {opportunityPageNumbers}
                
                                    currentPage = {currentOpportunityPage}
                                    setCurrentPage = {setCurrentOpportunityPage}
                                    itemsPerPage = {opportunitiesPerPage}
                                    setItemsPerPage = {setOpportunitiesPerPage}

                                    parentId = {"opportunitiesTR"}
                                />:null}
                            </td>
                        </tr>
                        :<></>}
                        {projects.length?
                        <tr id = "prejectsTR">
                            <th>Projects</th>
                            <td className = "position-relative pb10">
                                {projects.slice(indexOfFirstProject, indexOfLastProject).map((c, key) => <li key = {key}><Link to = {`/Projects/${c.projectHashKey}`}>{c.projectName}</Link></li>)}
                                {!showLoading && projects.length?
                                <Footer
                                    pageSelectId = "projects"
                                    length = {projects.length}
                                    pageNumbers = {projectPageNumbers}
                
                                    currentPage = {currentProjectPage}
                                    setCurrentPage = {setCurrentProjectPage}
                                    itemsPerPage = {projectsPerPage}
                                    setItemsPerPage = {setProjectsPerPage}

                                    parentId = {"prejectsTR"}
                                />:null}
                            </td>
                        </tr>
                        :<></>}
                    </tbody>
                </table>
                <div className = "d-flex flex-column flex-grow-1 maxWidth-50 mr-3">
                    <fieldset className = "border rounded flex-grow-1 px-3">
                        <legend className = "font-weight-bold w-auto px-2 mb-0">Background</legend>
                        <span className = "cssNotes">{curOrg.background}</span>
                    </fieldset>
                </div>
            </div>
            <div className = "text-right m-2">
                <button type = "button" className = "btn btn-info ml-2" onClick = {handleAddContact}>Add Contact</button>
                <button type = "button" className = "btn btn-primary ml-2" onClick = {handleEditOpen}>Edit</button>
                <button type = "button" className = "btn btn-danger ml-2" onClick = {handleDelete} id = {curOrg.organizationHashKey}>Delete</button>
            </div>
            {path3 === "Edit"?
            <OrganizationForm
                updateDateTime = {updateDateTime}
                closeIt = {() => history.push(`/${path1}/${curOrg.organizationHashKey}`)}
                history = {history} 
                keyword = "Edit"

                setOrganizations = {setOrganizations}
                organizations = {organizations}

                {...curOrg}
            />
            :null}
        </div>
    )
}